import { Plane, Clock, MapPin, Moon, AlertCircle } from "lucide-react";
import type { FlightDetails, JourneyDetails } from "@/types/travel";
import { formatTime } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";

interface FlightDetailsTimelineProps {
  details: FlightDetails;
}

function JourneyTimeline({ journey }: { journey: JourneyDetails }) {
  return (
    <div className="relative">
      {journey.segments.map((segment, index) => (
        <div key={index}>
          {/* Departure */}
          <div className="flex items-start gap-4">
            <div className="relative flex flex-col items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              {index < journey.segments.length - 1 || journey.layovers ? (
                <div className="h-20 w-0.5 bg-border" />
              ) : (
                <div className="h-16 w-0.5 bg-border" />
              )}
            </div>
            <div className="flex-1 pb-4">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold">{formatTime(segment.departureTime)}</span>
                <span className="text-sm text-gray-800 dark:text-muted-foreground">
                  {segment.departureAirport}
                  {segment.departureTerminal && ` • Terminal ${segment.departureTerminal}`}
                </span>
              </div>
            </div>
          </div>

          {/* Flight */}
          <div className="flex items-start gap-4">
            <div className="relative flex flex-col items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10 text-blue-500">
                <Plane className="h-5 w-5" />
              </div>
              <div className="h-16 w-0.5 bg-border" />
            </div>
            <div className="flex-1 pb-4">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{segment.airline}</span>
                  <span className="text-sm text-gray-800 dark:text-muted-foreground">
                    Flight {segment.flightNumber}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-800 dark:text-muted-foreground">
                    Duration: {segment.duration}
                  </span>
                  {segment.aircraft && (
                    <>
                      <span className="text-sm text-gray-800 dark:text-muted-foreground">•</span>
                      <span className="text-sm text-gray-800 dark:text-muted-foreground">{segment.aircraft}</span>
                    </>
                  )}
                  {journey.isOvernightFlight && (
                    <>
                      <span className="text-sm text-muted-foreground">•</span>
                      <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                        <Moon className="h-3 w-3" />
                        <span className="text-xs">Overnight</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Arrival */}
          <div className="flex items-start gap-4">
            <div className="relative flex flex-col items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              {journey.layovers && index < journey.layovers.length && (
                <div className="h-20 w-0.5 bg-orange-500/30" />
              )}
            </div>
            <div className="flex-1 pb-4">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold">{formatTime(segment.arrivalTime)}</span>
                <span className="text-sm text-gray-800 dark:text-muted-foreground">
                  {segment.arrivalAirport}
                  {segment.arrivalTerminal && ` • Terminal ${segment.arrivalTerminal}`}
                </span>
              </div>
            </div>
          </div>

          {/* Layover */}
          {journey.layovers && index < journey.layovers.length && (
            <div className="flex items-start gap-4">
              <div className="relative flex flex-col items-center">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  journey.layovers[index].isLongLayover 
                    ? 'bg-amber-500/10 text-amber-500' 
                    : 'bg-orange-500/10 text-orange-500'
                }`}>
                  {journey.layovers[index].isLongLayover ? (
                    <AlertCircle className="h-5 w-5" />
                  ) : (
                    <Clock className="h-5 w-5" />
                  )}
                </div>
                <div className="h-20 w-0.5 bg-border" />
              </div>
              <div className="flex-1 pb-4">
                <div className={`rounded-lg p-3 ${
                  journey.layovers[index].isLongLayover
                    ? 'bg-amber-50 dark:bg-amber-950/20'
                    : 'bg-orange-50 dark:bg-orange-950/20'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`text-sm font-medium ${
                      journey.layovers[index].isLongLayover
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-orange-600 dark:text-orange-400'
                    }`}>
                      {journey.layovers[index].duration} • Change planes in {journey.layovers[index].airport}
                    </p>
                    {journey.layovers[index].isLongLayover && (
                      <span className="text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 font-medium">
                        Long layover
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-800 dark:text-muted-foreground">
                    Time to explore the airport, grab a meal, or rest
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function FlightDetailsTimeline({ details }: FlightDetailsTimelineProps) {
  if (details.isRoundTrip && details.outbound && details.return) {
    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="flex items-center flex-wrap gap-2 text-sm text-gray-800 dark:text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>Total Duration: {details.totalDuration}</span>
          {details.co2Emissions && (
            <>
              <span className="mx-2">•</span>
              <span>CO₂: {details.co2Emissions}</span>
            </>
          )}
          {details.isOvernightFlight && (
            <>
              <span className="mx-2">•</span>
              <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Moon className="h-3 w-3" />
                <span className="font-medium">Includes overnight flight</span>
              </div>
            </>
          )}
        </div>

        {/* Depart Section */}
        <div>
          <h4 className="font-semibold text-lg mb-4 text-primary">Depart</h4>
          <JourneyTimeline journey={details.outbound} />
        </div>

        <Separator className="my-6" />

        {/* Return Section */}
        <div>
          <h4 className="font-semibold text-lg mb-4 text-primary">Return</h4>
          <JourneyTimeline journey={details.return} />
        </div>
      </div>
    );
  }

  // Single journey or multi-city layout (original)
  return (
    <div className="space-y-4">
      <div className="flex items-center flex-wrap gap-2 text-sm text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>Total Duration: {details.totalDuration}</span>
        {details.co2Emissions && (
          <>
            <span className="mx-2">•</span>
            <span>CO₂: {details.co2Emissions}</span>
          </>
        )}
        {details.isOvernightFlight && (
          <>
            <span className="mx-2">•</span>
            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
              <Moon className="h-3 w-3" />
              <span className="font-medium">Overnight flight</span>
            </div>
          </>
        )}
      </div>

      <div className="relative">
        {details.segments.map((segment, index) => (
          <div key={index}>
            {/* Departure */}
            <div className="flex items-start gap-4">
              <div className="relative flex flex-col items-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MapPin className="h-5 w-5" />
                </div>
                {index < details.segments.length - 1 || details.layovers ? (
                  <div className="h-20 w-0.5 bg-border" />
                ) : (
                  <div className="h-16 w-0.5 bg-border" />
                )}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold">{formatTime(segment.departureTime)}</span>
                  <span className="text-sm text-gray-800 dark:text-muted-foreground">
                    {segment.departureAirport}
                    {segment.departureTerminal && ` • Terminal ${segment.departureTerminal}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Flight */}
            <div className="flex items-start gap-4">
              <div className="relative flex flex-col items-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10 text-blue-500">
                  <Plane className="h-5 w-5" />
                </div>
                <div className="h-16 w-0.5 bg-border" />
              </div>
              <div className="flex-1 pb-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{segment.airline}</span>
                    <span className="text-sm text-gray-800 dark:text-muted-foreground">
                      Flight {segment.flightNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-800 dark:text-muted-foreground">
                      Duration: {segment.duration}
                    </span>
                    {segment.aircraft && (
                      <>
                        <span className="text-sm text-gray-800 dark:text-muted-foreground">•</span>
                        <span className="text-sm text-gray-800 dark:text-muted-foreground">{segment.aircraft}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Arrival */}
            <div className="flex items-start gap-4">
              <div className="relative flex flex-col items-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MapPin className="h-5 w-5" />
                </div>
                {details.layovers && index < details.layovers.length && (
                  <div className="h-20 w-0.5 bg-orange-500/30" />
                )}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold">{formatTime(segment.arrivalTime)}</span>
                  <span className="text-sm text-gray-800 dark:text-muted-foreground">
                    {segment.arrivalAirport}
                    {segment.arrivalTerminal && ` • Terminal ${segment.arrivalTerminal}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Layover */}
            {details.layovers && index < details.layovers.length && (
              <div className="flex items-start gap-4">
                <div className="relative flex flex-col items-center">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                    details.layovers[index].isLongLayover 
                      ? 'bg-amber-500/10 text-amber-500' 
                      : 'bg-orange-500/10 text-orange-500'
                  }`}>
                    {details.layovers[index].isLongLayover ? (
                      <AlertCircle className="h-5 w-5" />
                    ) : (
                      <Clock className="h-5 w-5" />
                    )}
                  </div>
                  <div className="h-20 w-0.5 bg-border" />
                </div>
                <div className="flex-1 pb-4">
                  <div className={`rounded-lg p-3 ${
                    details.layovers[index].isLongLayover
                      ? 'bg-amber-50 dark:bg-amber-950/20'
                      : 'bg-orange-50 dark:bg-orange-950/20'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`text-sm font-medium ${
                        details.layovers[index].isLongLayover
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-orange-600 dark:text-orange-400'
                      }`}>
                        {details.layovers[index].duration} • Change planes in {details.layovers[index].airport}
                      </p>
                      {details.layovers[index].isLongLayover && (
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 font-medium">
                          Long layover
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Time to explore the airport, grab a meal, or rest
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}